/**
 * One staff member's account, line by line.
 *
 * The staff list says what somebody owes. This says why: each meal that went on
 * their account, on which day and bill and what was on it, each refund that
 * came off, and each time the tab was cleared — with the balance after every
 * line, so the figure at the bottom is the one on the staff list.
 *
 * WHY IT READS BY DAY
 * -------------------
 * The question asked of this screen is "what did he eat on Friday", so the
 * lines are gathered under the trading day they belong to, newest day first. A
 * meal rung up after midnight sits under the night it was part of.
 *
 * WHY IT IS A PAGE AND NOT A PANEL
 * --------------------------------
 * A tab can run to dozens of bills, and it is handed to the person it belongs
 * to — printed, or sent as a file. A page has room for that and a sheet over
 * the staff list does not.
 */
import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { formatPKR, formatServerTimeOnly } from '@foodrun/shared';

import { api, withQuery } from '../../lib/api.js';
import { prettyDay } from '../reports/period.jsx';
import { ReportExport } from '../reports/ReportExport.jsx';
import { BillSheet } from '../pos/BillSheet.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { Spinner } from '../../components/ui/Spinner.jsx';

const KIND = {
  charge: { label: 'Meal', tone: 'bg-warn-bg text-warn' },
  refund: { label: 'Refund', tone: 'bg-good-bg text-good' },
  settlement: { label: 'Payment', tone: 'bg-good-bg text-good' },
};

/** Lines gathered under their day, newest day first, in the order they happened. */
export function groupByDay(entries) {
  const days = new Map();
  for (const entry of entries) {
    const key = entry.day ?? 'Undated';
    if (!days.has(key)) days.set(key, { day: key, entries: [], charged: 0 });
    const group = days.get(key);
    group.entries.push(entry);
    if (entry.type === 'charge') group.charged += entry.amount;
  }
  return [...days.values()].sort((a, b) => (a.day < b.day ? 1 : a.day > b.day ? -1 : 0));
}

export default function AccountHistoryPage() {
  const { id } = useParams();
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');

  const path = withQuery(`/admin/staff/${id}/account/history`, {
    from: from || undefined,
    to: to || undefined,
  });

  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['staff', 'account-history', id, from, to],
    queryFn: () => api.get(path),
    placeholderData: (previous) => previous,
  });

  const days = groupByDay(data?.entries ?? []);
  const narrowed = Boolean(from || to);

  // The bill open as a receipt to print, and the one being moved to another
  // person's account.
  const [printing, setPrinting] = useState(null);
  const [moving, setMoving] = useState(null);

  return (
    <div className="mx-auto max-w-4xl p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <Link to="/staff" className="text-sm font-medium text-brand-300 hover:underline">
            ← Staff
          </Link>
          <h1 className="mt-1 font-semibold text-xl text-ink">
            {data?.staff?.name ?? 'Staff account'}
          </h1>
          <p className="mt-0.5 text-sm text-ink-2">
            Everything put on this account, and everything taken off it.
          </p>
        </div>

        {data && (
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="secondary" onClick={() => window.print()}>
              Print
            </Button>
            <ReportExport path={path} name={`Staff account - ${data.staff.name}`} />
          </div>
        )}
      </div>

      <div className="mb-4 flex flex-wrap items-end gap-3">
        <label className="text-sm text-ink-2">
          <span className="mb-1 block">From</span>
          <input
            type="date"
            value={from}
            max={to || undefined}
            onChange={(e) => setFrom(e.target.value)}
            className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-ink"
          />
        </label>
        <label className="text-sm text-ink-2">
          <span className="mb-1 block">To</span>
          <input
            type="date"
            value={to}
            min={from || undefined}
            onChange={(e) => setTo(e.target.value)}
            className="h-11 rounded-lg border border-rule bg-surface-3 px-3 text-ink"
          />
        </label>
        {narrowed && (
          <Button
            variant="secondary"
            onClick={() => {
              setFrom('');
              setTo('');
            }}
          >
            Show everything
          </Button>
        )}
      </div>

      {isLoading && !data ? (
        <div className="flex h-[40vh] items-center justify-center">
          <Spinner label="Loading" />
        </div>
      ) : isError || !data ? (
        <p role="alert" className="py-20 text-center text-sm text-ink-2">
          {error?.message ?? 'Could not load this account.'}
        </p>
      ) : (
        <div className="statement rounded-xl border border-rule bg-surface p-5">
          <div className="flex flex-wrap items-baseline justify-between gap-2">
            <h2 className="font-semibold text-lg text-ink">{data.staff.name} — staff account</h2>
            <p className="text-sm text-ink-2">
              {narrowed
                ? `${from ? prettyDay(from) : 'The start'} to ${to ? prettyDay(to) : 'today'}`
                : 'Everything on the account'}
            </p>
          </div>

          <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Figure label="Owed now" value={formatPKR(data.balance)} strong />
            <Figure label="Meals charged" value={formatPKR(data.totals.charged)} />
            <Figure label="Refunded" value={formatPKR(data.totals.refunded)} />
            <Figure label="Paid" value={formatPKR(data.totals.settled)} />
          </dl>

          {narrowed && (
            <p className="mt-3 text-sm text-ink-2">
              Owed before {from ? prettyDay(from) : 'this period'}:{' '}
              <span className="font-medium text-ink tabular-nums">{formatPKR(data.opening)}</span>
              {' · '}Owed at the end of it:{' '}
              <span className="font-medium text-ink tabular-nums">{formatPKR(data.closing)}</span>
            </p>
          )}

          {days.length === 0 ? (
            <p className="py-12 text-center text-sm text-ink-2">
              {narrowed
                ? 'Nothing went on or came off this account in these dates.'
                : 'Nothing has ever been put on this account.'}
            </p>
          ) : (
            days.map((group) => (
              <section key={group.day} className="mt-5 break-inside-avoid">
                <div className="flex items-baseline justify-between gap-2 border-b border-rule pb-1.5">
                  <h3 className="font-semibold text-ink">
                    {group.day === 'Undated' ? 'Undated' : prettyDay(group.day)}
                  </h3>
                  {group.charged > 0 && (
                    <p className="text-sm text-ink-2 tabular-nums">
                      {formatPKR(group.charged)} charged
                    </p>
                  )}
                </div>

                <ul>
                  {group.entries.map((entry) => (
                    <Entry
                      key={entry.id}
                      entry={entry}
                      onPrint={() => setPrinting(entry.orderId)}
                      onMove={() =>
                        // What is still owed on the bill: the meal, less what
                        // has been refunded off it.
                        setMoving({
                          ...entry,
                          amount: Math.max(
                            0,
                            entry.amount -
                              (data.entries ?? [])
                                .filter((e) => e.type === 'refund' && e.orderId === entry.orderId)
                                .reduce((sum, e) => sum + e.amount, 0)
                          ),
                        })
                      }
                    />
                  ))}
                </ul>
              </section>
            ))
          )}
        </div>
      )}

      {/* The receipt, for looking at and printing only — nothing is refunded or
          discounted from a history. */}
      {printing && (
        <BillSheet orderId={printing} canRefund={false} onClose={() => setPrinting(null)} />
      )}

      {moving && data && (
        <MoveBillSheet
          entry={moving}
          from={data.staff}
          onClose={() => setMoving(null)}
        />
      )}
    </div>
  );
}

/**
 * Moving one bill to somebody else's account.
 *
 * For a meal put on the wrong person — the name above or below picked at the
 * till. The bill goes across with what is still owed on it, and it is said in
 * plain figures before it happens, because it changes what two people owe.
 */
function MoveBillSheet({ entry, from, onClose }) {
  const client = useQueryClient();
  const [toId, setToId] = useState('');

  const staff = useQuery({
    queryKey: ['staff', 'active'],
    queryFn: () => api.get('/admin/staff'),
  });
  // Anybody on the list who can still be asked to pay, except whose it is now.
  const people = (staff.data?.staff ?? []).filter((p) => p.id !== from.id && p.is_active);
  const to = people.find((p) => p.id === toId);

  const move = useMutation({
    mutationFn: () =>
      api.post(`/admin/staff/${from.id}/account/transfer`, { entryId: entry.id, toStaffId: toId }),
    onSuccess: () => {
      // Both tabs, the staff list's balances and the reports all moved.
      client.invalidateQueries({ queryKey: ['staff'] });
      client.invalidateQueries({ queryKey: ['reports'] });
      onClose();
    },
  });

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={`Move bill ${entry.orderNumber}`}
      className="fixed inset-0 z-30 flex items-start justify-center overflow-y-auto bg-black/70 p-4"
    >
      <div className="w-full max-w-md rounded-2xl bg-surface p-5">
        <h2 className="font-semibold text-lg text-ink">Move this bill to another account</h2>
        <p className="mt-1 text-sm text-ink-2">
          {entry.orderNumber} &middot; {formatPKR(entry.amount)} &middot; now on {from.name}
          &rsquo;s account
        </p>

        <label className="mt-4 block text-sm text-ink-2">
          <span className="mb-1.5 block font-medium">Move it to</span>
          <select
            aria-label="Move it to"
            value={toId}
            onChange={(e) => setToId(e.target.value)}
            className="h-12 w-full rounded-lg border border-rule bg-surface-3 px-3 text-[15px] text-ink focus:border-brand-400 focus:outline-none focus:ring-2 focus:ring-brand-400/30"
          >
            <option value="">{staff.isLoading ? 'Loading…' : 'Choose a staff member'}</option>
            {people.map((p) => (
              <option key={p.id} value={p.id}>
                {`${p.first_name} ${p.last_name}`.trim()}
              </option>
            ))}
          </select>
        </label>

        {to && (
          <p className="mt-3 rounded-lg bg-warn-bg px-3.5 py-2.5 text-sm text-warn">
            {from.name} will owe {formatPKR(entry.amount)} less, and {to.first_name} will owe that
            much more. Anything already refunded on this bill moves with it.
          </p>
        )}

        {move.isError && (
          <p role="alert" className="mt-3 rounded-lg bg-bad-bg px-3.5 py-2.5 text-sm text-bad">
            {move.error?.message ?? 'That did not go through.'}
          </p>
        )}

        <div className="mt-5 flex gap-2">
          <Button variant="secondary" size="lg" onClick={onClose} disabled={move.isPending}>
            Go back
          </Button>
          <Button
            size="lg"
            className="flex-1"
            busy={move.isPending}
            disabled={!toId}
            onClick={() => move.mutate()}
          >
            {to ? `Move to ${to.first_name}` : 'Move bill'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Figure({ label, value, strong }) {
  return (
    <div className="rounded-lg border border-rule px-3.5 py-2.5">
      <dt className="text-xs text-ink-2">{label}</dt>
      <dd className={`mt-0.5 tabular-nums ${strong ? 'font-semibold text-lg text-warn' : 'font-medium text-ink'}`}>
        {value}
      </dd>
    </div>
  );
}

function Entry({ entry, onPrint, onMove }) {
  const kind = KIND[entry.type] ?? { label: entry.type, tone: 'bg-surface-3 text-ink-2' };
  const meal = entry.type === 'charge';
  // A cancelled bill has nothing on it to move.
  const movable = meal && entry.orderId && !entry.cancelled;

  return (
    <li className="break-inside-avoid border-b border-rule/60 py-3 last:border-b-0">
      <div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-1">
        <div className="min-w-0">
          <p className="flex flex-wrap items-center gap-2 text-sm">
            <span className={`rounded-full px-2 py-0.5 text-xs font-medium ${kind.tone}`}>
              {kind.label}
            </span>
            {entry.orderNumber && <span className="font-medium text-ink">{entry.orderNumber}</span>}
            {entry.cancelled && <span className="text-xs text-bad">Bill cancelled</span>}
            <span className="text-ink-3">
              {formatServerTimeOnly(entry.createdAt)}
              {entry.recordedBy ? ` · by ${entry.recordedBy}` : ''}
            </span>
          </p>
          {entry.note && <p className="mt-1 text-sm text-ink-2">{entry.note}</p>}

          {/* Each bill on its own: its receipt, and a way to put it on the
              right person's account. Left off the printed statement. */}
          {meal && entry.orderId && (
            <p className="no-print mt-1.5 flex gap-1">
              <button
                type="button"
                onClick={onPrint}
                aria-label={`Print bill ${entry.orderNumber}`}
                className="rounded-lg px-2 py-1 text-xs font-medium text-brand-300 hover:bg-brand-400/15"
              >
                Print bill
              </button>
              {movable && (
                <button
                  type="button"
                  onClick={onMove}
                  aria-label={`Move bill ${entry.orderNumber} to another account`}
                  className="rounded-lg px-2 py-1 text-xs font-medium text-brand-300 hover:bg-brand-400/15"
                >
                  Move to another account
                </button>
              )}
            </p>
          )}
        </div>

        <div className="text-right">
          <p className={`font-medium tabular-nums ${meal ? 'text-ink' : 'text-good'}`}>
            {meal ? '' : '− '}
            {formatPKR(entry.amount)}
          </p>
          <p className="text-xs text-ink-3 tabular-nums">Owed after: {formatPKR(entry.balanceAfter)}</p>
        </div>
      </div>

      {meal && entry.items.length > 0 && (
        <table className="mt-2 w-full text-sm">
          <tbody>
            {entry.items.map((item, index) => (
              <tr key={index} className={item.refunded ? 'text-ink-3 line-through' : 'text-ink-2'}>
                <td className="w-10 py-0.5 pr-2 align-top tabular-nums">{item.quantity} ×</td>
                <td className="py-0.5 pr-2">
                  {item.name}
                  {item.notes ? <span className="text-ink-3"> — {item.notes}</span> : null}
                </td>
                <td className="py-0.5 text-right align-top tabular-nums">{formatPKR(item.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </li>
  );
}
